import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { ElearningDataType } from '@/modules/types/elearning';
import { FrameDataType } from '@/modules/types/frame';
import { FaqType, HelperType } from '@/modules/types/helper';
import {
  QuizAttemptResponseType,
  QuizAttemptStartResponseType,
  QuizDataType,
} from '@/modules/types/quiz';
import { ResourceType } from '@/modules/types/resource';
import { ReactElement } from 'react';
import { ElearningBehaviourSettingsDataInterface } from '@/modules/types/tenant';

export const elearningPlayerAdaptor = createEntityAdapter();

export interface ElearningPlayerStateType {
  isPreview: boolean;
  isLive: boolean;
  isPlayable: boolean;
  elearningBehaviourSettings: ElearningBehaviourSettingsDataInterface;
  isQuizReady: boolean;
  isVisibleMenu: boolean;
  isVisibleHelper: boolean;
  isVisibleResources: boolean;
  isVisibleQuizHelper: boolean;
  content: {
    elearning?: ElearningDataType;
    frames?: FrameDataType[];
    currentFrameId?: string;
    quiz?: QuizDataType;
    resources?: ResourceType[];
    helperContent?: HelperType[];
    helperFaq?: FaqType[];
    canvas?: ReactElement;
    quizStartAttemptData?: QuizAttemptStartResponseType | null;
    quizGetAttemptData?: QuizAttemptResponseType | null;
  };
  handlers: {
    submitResult: (result: any, callback: (payload: any) => void) => void;
    submitQuestion: (answer: any, callback: (payload: any) => void) => void;
    getQuestions: () => void;
    submitAssessment: (quizData: any, callback: (payload: any) => void) => void;
    startAssessment: (
      getQuizAttemptData: QuizAttemptResponseType | null | undefined,
    ) => void; // should I change this?
    moveToFrame: (nextFrameId: string) => void;
  };
}

const initialState: ElearningPlayerStateType =
  elearningPlayerAdaptor.getInitialState({
    isPreview: false,
    isLive: false,
    isPlayable: false,
    elearningBehaviourSettings: {
      tenantId: '',
      elearningBookmarkEnable: false,
      courseTrackingEnable: false,
      videoLibraryEnabled: false,
      libraryEnabled: false,
    },
    isQuizReady: false,
    isVisibleMenu: false,
    isVisibleHelper: false,
    isVisibleResources: false,
    isVisibleQuizHelper: false,
    content: {},
    handlers: {
      submitResult: () => {},
      submitQuestion: (answer: any) => {},
      getQuestions: () => {},
      submitAssessment: (quizData: any) => {},
      startAssessment: () => {},
      moveToFrame: (nextFrameId: string) => {},
    },
  });

const elearningPlayerSlice = createSlice({
  name: 'elearningPlayer',
  initialState,
  reducers: {
    setPreview: (state) => {
      state.isPreview = true;
    },
    setLive: (state) => {
      state.isLive = true;
    },
    setPlayable: (state) => {
      state.isPlayable = true;
    },
    setElearningBehaviourSettings: (state, action) => {
      state.elearningBehaviourSettings = action.payload;
    },
    enableQuiz: (state, action) => {
      state.isQuizReady = !!action.payload;
    },
    disableQuiz: (state) => {
      state.isQuizReady = false;
    },
    disableUI: (state) => {
      state.isPlayable = false;
    },
    setCurrentFrameId: (state, action) => {
      state.content.currentFrameId = action.payload.currentFrameId;
    },
    setContent: (state, action) => {
      state.content = action.payload;
    },
    setHandlers: (state, action) => {
      state.handlers = action.payload;
    },
    toggleQuizHelper: (state) => {
      state.isVisibleQuizHelper = !state.isVisibleQuizHelper;
    },
    openQuizHelper: (state) => {
      state.isVisibleQuizHelper = true;
    },
    closeQuizHelper: (state) => {
      state.isVisibleQuizHelper = false;
    },
    toggleMenu: (state) => {
      state.isVisibleMenu = !state.isVisibleMenu;
      state.isVisibleResources = false;
      state.isVisibleHelper = false;
    },
    openMenu: (state) => {
      state.isVisibleMenu = true;
      state.isVisibleHelper = false;
      state.isVisibleResources = false;
    },
    closeMenu: (state) => {
      state.isVisibleMenu = false;
    },
    toggleResources: (state) => {
      state.isVisibleResources = !state.isVisibleResources;
      state.isVisibleMenu = false;
      state.isVisibleHelper = false;
    },
    openResources: (state) => {
      state.isVisibleResources = true;
      state.isVisibleMenu = false;
      state.isVisibleHelper = false;
    },
    closeResources: (state) => {
      state.isVisibleResources = false;
    },
    toggleHelper: (state) => {
      state.isVisibleHelper = !state.isVisibleHelper;
      state.isVisibleMenu = false;
      state.isVisibleResources = false;
    },
    openHelper: (state) => {
      state.isVisibleHelper = true;
      state.isVisibleMenu = false;
      state.isVisibleResources = false;
    },
    closeHelper: (state) => {
      state.isVisibleHelper = false;
    },

    reset: (state) => {
      state.isVisibleMenu = false;
      state.isVisibleHelper = false;
      state.isVisibleResources = false;
    },
    setQuizStartAttempt: (state, action) => {
      state.content.quizStartAttemptData = action.payload;
    },
    setQuizGetAttempt: (state, action) => {
      state.content.quizGetAttemptData = action.payload;
    },
  },
});

export const { name, actions, reducer } = elearningPlayerSlice;
